import { Printer } from "lucide-react";

const ATT = "https://customer-assets-lxgj4vgw.emergentagent.net/wingman/2cb802e7-a348-4a37-966c-0adc15c0e209/attachments/";
const SHOW_IMGS = [
  `${ATT}b491d823f0a2420a96bbcfd52b22e00f_image.png`,
  `${ATT}8d011318698446f59660af64a6b4f19c_image.png`,
  `${ATT}387eff7aec524ffc9f1682e8a42f78de_image.png`,
  `${ATT}4251af794f6d478fa12701d625359724_image.png`,
];

const SectionTitle = ({ children }) => (
  <h2 className="font-sans text-[11px] tracking-[0.35em] uppercase text-[#c1683f] mb-5">{children}</h2>
);

export default function Dossier() {
  return (
    <div data-testid="dossier-page" className="min-h-screen bg-[#0d0d0d] text-[#f3ecdf] font-serif print:bg-[#0d0d0d]">
      <style>{`
        @media print {
          body { background: #0d0d0d !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .dossier-section { break-inside: avoid; }
        }
      `}</style>

      {/* HERO */}
      <header className="relative h-[92vh] min-h-[560px] overflow-hidden dossier-section" data-testid="dossier-hero">
        <img src="/stage/lux-carlo-curd.webp" alt="Carlo pulling mozzarella, night shot" className="absolute inset-0 w-full h-full object-cover object-[50%_25%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/60 via-[#0d0d0d]/30 to-[#0d0d0d]" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <p className="font-sans text-[12px] tracking-[0.5em] uppercase text-[#f3ecdf]/90">Filata Experience</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-normal leading-tight max-w-3xl">Artisan Mozzarella &amp; Live Show Cooking</h1>
          <p className="mt-6 font-sans text-sm tracking-[0.3em] uppercase text-[#d9a98f]">Resort Partnership Dossier</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 pb-24">
        {/* 1 — THE EXPERIENCE */}
        <section className="pt-16 dossier-section" data-testid="dossier-experience">
          <SectionTitle>01 · The Experience</SectionTitle>
          <p className="text-lg leading-relaxed text-[#e7ddd0]">
            Filata Experience brings a third-generation Italian master cheesemaker directly into the resort — daily fresh production of mozzarella and burrata from imported curd, combined with live show cooking that transforms a meal into a genuinely memorable guest moment. Rooted in Puglia, the birthplace of burrata, and built for the luxury resort environment.
          </p>
        </section>

        {/* 2 — THE ARTISAN */}
        <section className="pt-16 dossier-section" data-testid="dossier-artisan">
          <SectionTitle>02 · The Artisan</SectionTitle>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <img src="/stage/carlo-editorial.webp" alt="Carlo at the pot, steam rising" className="w-full rounded-sm object-cover aspect-[3/4]" />
            <div>
              <h3 className="text-xl text-white leading-snug">Carlo Sansanelli — Master Cheesemaker, Third Generation</h3>
              <p className="mt-4 text-sm leading-relaxed text-[#cbbfb2]">
                In 1950, the Sansanelli family began a tradition of artisan dairy farming in the Puglia countryside. In 1989, Carlo Sansanelli and his son Michele founded Caseificio Biancolatte in Bari — a craft dairy built on three generations of knowledge, hands, and passion. Carlo, the third generation, grew up working alongside his father. Over time he developed a vision: bringing the live spectacle of mozzarella-making to luxury hospitality experiences worldwide. That vision became Filata Experience.
              </p>
            </div>
          </div>
        </section>

        {/* 3 — THE PRODUCTION */}
        <section className="pt-16 dossier-section" data-testid="dossier-production">
          <SectionTitle>03 · The Production</SectionTitle>
          <p className="text-base leading-relaxed text-[#e7ddd0]">
            Every morning, the Filata team produces up to 200kg of fresh mozzarella and burrata per week, working from imported curd to our technical specifications. The product reaches the resort's kitchen and table the same day it is made — no compromise on freshness, no industrial process. The team operates with full autonomy on setup, production, and show cooking preparation. Standard kitchen clearing is the only requirement from the property.
          </p>
        </section>

        {/* 4 — THE SHOW */}
        <section className="pt-16 dossier-section" data-testid="dossier-show">
          <SectionTitle>04 · The Show</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            {SHOW_IMGS.map((src, i) => (
              <img key={i} src={src} alt={`Live show cooking ${i + 1}`} className="w-full aspect-[4/3] object-cover rounded-sm" data-testid={`dossier-show-img-${i + 1}`} />
            ))}
          </div>
          <p className="mt-6 text-base leading-relaxed text-[#e7ddd0]">
            Four live show cooking sessions per week, scheduled to the property's programming — breakfast, lunch, dinner, poolside, beach, or private events. Authentic Pugliese dishes prepared live: <em className="text-[#d9a98f]">Cavatelli di mare · Orecchiette con cime di rape e stracciatella fresca · Spaghetti con gamberi e pesto di pistacchi · Pasta con pesce spada e pomodorini confit</em>. One 90cm pot serves up to 50 guests; the format scales to 200 guests for large events.
          </p>
        </section>

        {/* 5 — THE FLEXIBILITY */}
        <section className="pt-16 dossier-section" data-testid="dossier-flexibility">
          <SectionTitle>05 · The Flexibility</SectionTitle>
          <p className="text-base leading-relaxed text-[#e7ddd0]">
            The production-to-show ratio adapts to the property's needs. Higher show frequency can be prioritised over volume production, or vice versa — the team adjusts to the resort's seasonal calendar and occupancy patterns.
          </p>
        </section>

        {/* 6 — WHAT WE BRING / WHAT YOU PROVIDE */}
        <section className="pt-16 dossier-section" data-testid="dossier-columns">
          <SectionTitle>06 · The Framework</SectionTitle>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-[#f3ecdf]/15 rounded-sm p-6 bg-[#f3ecdf]/[0.03]">
              <h3 className="font-sans text-xs tracking-[0.25em] uppercase text-[#d9a98f] mb-4">What We Bring</h3>
              <ul className="space-y-3 text-sm text-[#cbbfb2] leading-relaxed list-none">
                <li>— Third-generation master cheesemaker and full operational team</li>
                <li>— Complete show cooking setup and artisan workstation</li>
                <li>— Daily fresh production to Italian artisan standards</li>
                <li>— Full autonomy on setup, production, and show execution</li>
                <li>— Technical specifications for curd sourcing</li>
              </ul>
            </div>
            <div className="border border-[#f3ecdf]/15 rounded-sm p-6 bg-[#f3ecdf]/[0.03]">
              <h3 className="font-sans text-xs tracking-[0.25em] uppercase text-[#d9a98f] mb-4">What You Provide</h3>
              <ul className="space-y-3 text-sm text-[#cbbfb2] leading-relaxed list-none">
                <li>— Curd sourced to our technical specifications</li>
                <li>— Show cooking equipment (one-time investment, permanent asset of the property)</li>
                <li>— Standard kitchen clearing services</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 7 — THE PARTNERSHIP MODEL */}
        <section className="pt-16 dossier-section" data-testid="dossier-partnership">
          <SectionTitle>07 · The Partnership Model</SectionTitle>
          <p className="text-base leading-relaxed text-[#e7ddd0]">
            Filata Experience operates on a straightforward weekly partnership fee covering full artisan production and live show programming. No revenue sharing complexity, no POS integration — a clean, predictable cost with measurable guest experience impact. The format positions as a turnkey F&amp;B add-on: incremental revenue for the property, zero impact on existing kitchen operations, and a guest experience difficult to replicate anywhere else — because it requires a craftsman that takes three generations to make.
          </p>
        </section>

        {/* 8 — CONTACT */}
        <section className="pt-20 text-center dossier-section" data-testid="dossier-contact">
          <div className="w-10 h-px bg-[#c1683f] mx-auto mb-8" />
          <p className="text-xl text-white">Raffaele Orlando</p>
          <p className="mt-2 font-sans text-sm text-[#cbbfb2]">Filata Experience | Mare Verde S.R.L.S.</p>
          <p className="mt-1 font-sans text-sm text-[#d9a98f]">www.filataexperience.com</p>
          <p className="mt-1 font-sans text-sm text-[#d9a98f]">info@filataexperience.com</p>
        </section>
      </main>

      <button
        data-testid="dossier-print-btn"
        onClick={() => window.print()}
        className="no-print fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-[#c1683f] text-white font-sans text-sm px-6 py-3 shadow-lg hover:bg-[#a3542f] transition-colors"
      >
        <Printer className="h-4 w-4" /> Print / Save PDF
      </button>
    </div>
  );
}
